import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, Typography, Grid2 as Grid, Divider, Modal, IconButton, CircularProgress, LinearProgress, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useStyles } from './styles';
import { getAllExtractionDocumentTypes } from '../../../utils/ApiFunctions/ExtractionData';
import { getMainStyles } from '../../../styles/styles';
import LoadBar from '../../../utils/LoadBar';
import { ReactSVG } from 'react-svg';
import { IconSvg } from '../../../utils/globalIcons';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setExtractionDocumentTypesState } from '../../../Redux/slice/dataSlice';
import { useAppContext } from '../../../context/AppContext';
import { tostAlert } from '../../../utils/AlertToast';
import { CheckCircle, Close, ErrorOutline } from '@mui/icons-material';
import { handleExecuteExtraction } from './extraction';
import { useCheckCredit } from '../../../utils';
import ExtractionTraining from '../../../components/ExtractionTraining';

function ExtractionDocumentTypes() {

    const theme = useTheme();
    const styles = useStyles(theme);
    const mainStyles = getMainStyles(theme);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pollingIntervalRef = useRef(null);
    const { selectedProject, handleExtractionSteps, authData, initialAuthData, updateAuthData } = useAppContext();
    const isOutOfCredits = useCheckCredit();

    const [loading, setLoading] = useState(false)
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentTypeId, setDocumentTypeId] = useState(null);
    const [documentType, setDocumentType] = useState(null);
    const [selectedDocType, setSelectedDocType] = useState(null)
    const [previewMode, setPreviewMode] = useState("document");

    const modalDetails = { modalstatus: false, document_name: null, document_type_name: '', master_document_type: '' };
    const [modalData, setModalData] = useState(modalDetails);
    const extraction_document_types = useSelector(state => state.all_data.extraction_document_types)
    const [extractionState, setExtractionState] = useState({ status: 'idle', message: '', progress: 0 });
    const validTypes = ["ext_user_singin", "ext_existing_user", "ext_user_signup"];

    useEffect(() => {
        getDocumentTypes()
        //eslint-disable-next-line
    }, [])

    const getDocumentTypes = async () => {
        setLoading(true)
        try {
            let doc_type = []
            if (extraction_document_types?.length > 0) {
                doc_type = extraction_document_types
            } else {
                const project_id = selectedProject?.payload?.__auto_id__
                const response = await getAllExtractionDocumentTypes(project_id);
                doc_type = response
                dispatch(setExtractionDocumentTypesState(response))
            }

            const userDocumentType = authData?.document_type ?? null;
            const requestType = authData?.request_type ?? null;

            if (validTypes.includes(requestType) && userDocumentType) {
                const filtered_by_docs = doc_type?.filter(item => item?.payload?.document_type_category === userDocumentType) ?? [];
                setSelectedDocType(filtered_by_docs[0] ?? null)
                setDocumentTypes(filtered_by_docs)
            } else {
                setDocumentTypes(doc_type)
            }

            return doc_type
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const handleConfigure = async (doc_type_id, doc_type) => {
        if (isOutOfCredits) {
            tostAlert('Your credits have run out. Please top up account to continue.', 'error')
            return
        }
        handleExtractionSteps('review_extracted_fields_completed')
        setDocumentTypeId(doc_type_id)
        setDocumentType(doc_type)
        setPreviewMode("train")
    }

    const handleAddDocumentType = async () => {
        if (isOutOfCredits) {
            tostAlert('Your credits have run out. Please top up account to continue.', 'error')
            return
        }
        navigate('/document-types/add')
    }

    const handleDocumentExtraction = async () => {

        if (isOutOfCredits) {
            tostAlert('Your credits have run out. Please top up account to continue.', 'error')
            return
        }

        const document_type_id = selectedDocType?.payload?.__auto_id__;
        const document_type_name = selectedDocType?.payload?.master_document_type;
        const platformFileId = authData?.file_id ?? null;

        if (!platformFileId) {
            tostAlert("Extraction failed", "error")
            navigate("/all-documents")
            return
        }

        try {
            setModalData({ ...modalData, modalstatus: true });
            updateAuthData(initialAuthData);
            setExtractionState({ status: 'fetching', message: 'Fetching file...', progress: 20 });

            const { fileId, fileUrl, fileName, pipeline_exec_id } = await handleExecuteExtraction(document_type_id, document_type_name, platformFileId,
                pollingIntervalRef, setExtractionState, navigate);

            setExtractionState({ status: 'completed', message: 'Extraction completed!', progress: 100 });
            setTimeout(() => { handleModalClose() }, 1000);

            navigate(`/all-documents/${fileName}`, {
                state: {
                    file_id: fileId,
                    file_path: fileUrl,
                    file_name: fileName,
                    task_id: pipeline_exec_id,
                    approval_status: "PENDING"
                }
            })

        } catch (error) {
            console.error(error);
            setExtractionState({ status: 'failed', message: error.message || 'Extraction failed. Please try again.', progress: 0 });
            tostAlert("Extraction failed", "error")
            navigate("/all-documents")
        }
    }

    const handleModalClose = () => {
        setModalData(modalDetails);
    }

    return (
        <div style={styles.mainContainer}>

            {loading && <LoadBar />}


            {documentTypeId && documentType && previewMode === "train" ?
                (<ExtractionTraining
                    document_type_id={documentTypeId}
                    document_type={documentType}
                    setPreviewMode={setPreviewMode}
                />)
                :
                (<Box sx={styles.cardContainer}>

                    <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                        <Typography sx={styles.mainHeadingText}>Document Types</Typography>

                        {validTypes.includes(authData?.request_type)
                            ?
                            (<Button
                                sx={{ ...styles.saveBtn, width: '160px' }}
                                onClick={handleDocumentExtraction}
                            >
                                <Typography sx={styles.btnText}>
                                    Extract Data
                                </Typography>
                            </Button>)
                            :
                            (<Button
                                sx={{ ...styles.saveBtn, width: '190px' }}
                                onClick={handleAddDocumentType}
                            >
                                <Typography sx={styles.btnText}>
                                    Add Document Type
                                </Typography>
                            </Button>)
                        }
                    </Box>

                    <Box marginTop={'50px'} marginBottom={'20px'}>
                        <Grid container spacing={3}>
                            {documentTypes && documentTypes?.length > 0 ? documentTypes?.map((doc_type, index) => {

                                const is_external = validTypes.includes(authData?.request_type);
                                const is_selected = doc_type?.payload?.__auto_id__ === selectedDocType?.payload?.__auto_id__;

                                return (
                                    <Grid size={{ xs: 6, md: 4 }} key={index}
                                        onClick={() => is_external && setSelectedDocType(doc_type)}
                                    >
                                        <Card sx={{
                                            borderRadius: '10px',
                                            border: `solid ${is_selected ? "1.5px #c2c2c2" : "1px #DEDEDE"}`,
                                            cursor: is_external ? "pointer" : "default"
                                        }}>
                                            <Box padding={'20px 20px'}>
                                                <Box sx={styles.cardHeader}>
                                                    <Tooltip title={doc_type?.payload?.name} arrow>
                                                        <Typography sx={styles.cardHeadingText}>
                                                            {doc_type?.payload?.name}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                            </Box>

                                            {!is_external && <Divider />}
                                            {!is_external &&
                                                (<Box display={'flex'} alignItems={'center'}  >
                                                    <Box
                                                        sx={{
                                                            width: '50%',
                                                            "&:hover": {
                                                                backgroundColor: '#DEDEDE', color: '#0B51C5', textDecoration: 'underline',
                                                                "& svg": { stroke: '#ffffff' }
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', cursor: 'pointer' }}
                                                            onClick={() => handleConfigure(doc_type?.payload?.__auto_id__, doc_type?.payload?.master_document_type)}
                                                        >
                                                            <Box>
                                                                <ReactSVG
                                                                    src={IconSvg.configureIcon}
                                                                    beforeInjection={(svg) => {
                                                                        svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography sx={{ marginLeft: '5px' }}>Configure</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            width: '50%', borderLeft: "0.1px solid #0000001F",
                                                            "&:hover": {
                                                                backgroundColor: '#DEDEDE', color: '#0B51C5', textDecoration: 'underline',
                                                                "& svg": { stroke: '#ffffff' }
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', cursor: 'pointer' }}
                                                            onClick={() => navigate(`/document-types/files/${doc_type?.payload?.__auto_id__}`, {
                                                                state: {
                                                                    'doc_type_name': doc_type?.payload?.name,
                                                                    'doc_type': doc_type?.payload?.master_document_type,
                                                                }
                                                            })}
                                                        >
                                                            <Box>
                                                                <ReactSVG
                                                                    src={IconSvg.viewIcon}
                                                                    beforeInjection={(svg) => {
                                                                        svg.setAttribute('style', 'width: 18px; height:18px; display: block;');
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Typography sx={{ marginLeft: '5px' }}>View Documents</Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>)
                                            }

                                        </Card>
                                    </Grid>
                                )
                            })
                                :
                                (documentTypes?.length === 0 && !loading) && (
                                    <Box sx={mainStyles.noRecord}>
                                        <Typography sx={styles.noRecordsText}>No Records To Display</Typography>
                                    </Box>
                                )}
                        </Grid>
                    </Box>
                </Box>)
            }

            <Modal
                open={modalData.modalstatus}
                onClose={extractionState.status === 'extracting' ? undefined : handleModalClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={mainStyles.modalcontainer}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: '600' }}>
                            {extractionState.status === 'completed' ? 'Extraction Complete' :
                                extractionState.status === 'failed' ? 'Extraction Failed' :
                                    'Processing Document'}
                        </Typography>
                        {extractionState.status !== 'extracting' && (
                            <IconButton onClick={handleModalClose}>
                                <Close />
                            </IconButton>
                        )}
                    </Box>

                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        borderRadius: '10px', justifyContent: "center", padding: '70px', gap: '20px'
                    }}>
                        {extractionState.status === 'failed' ? (
                            <>
                                <ErrorOutline sx={{ fontSize: 60, color: 'error.main' }} />
                                <Typography color="error">{extractionState.message}</Typography>
                                <Button variant="contained" onClick={handleModalClose}>
                                    Close
                                </Button>
                            </>
                        ) : extractionState.status === 'completed' ? (
                            <>
                                <CheckCircle sx={{ fontSize: 60, color: 'success.main' }} />
                                <Typography color="success.main">{extractionState.message}</Typography>
                            </>
                        ) : (
                            <>
                                <CircularProgress size={60} />
                                <Typography sx={{ mt: 2, textAlign: 'center' }}>{extractionState.message}</Typography>
                                <LinearProgress variant="determinate" value={extractionState.progress} sx={{ width: '100%', mt: 1 }} />
                            </>
                        )}
                    </Box>
                </Box>
            </Modal>

        </div >

    )
}

export default ExtractionDocumentTypes;